import { RightOutlined } from '@ant-design/icons';
import { Alert, Collapse } from 'antd';
import { educationParallelTextRepresentation } from 'entities/education-class';
import { translateDay } from 'features/schedule/utils';
import { AppSider } from 'shared/UI/UIKit/Sider';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppText } from 'shared/UI/UIKit/Text';
import { type DayOfWeek } from 'services/api';
import type { MaybeType } from 'shared/types';
import type { ScheduleReportStatus } from 'services/api';
import { useViolationsDrawer, getSiderTitle, getViolationLabel } from '../model';
import styles from './styles.module.scss';

type ViolationsDrawerProps = {
  open: boolean;
  onClose: () => void;
  type: MaybeType<ScheduleReportStatus>;
};

export const ViolationsDrawer = ({ open, onClose, type }: ViolationsDrawerProps) => {
  const {
    state: { violations, activeKeys },
    methods: { togglePanelKey, handleViolationLessonClick },
  } = useViolationsDrawer(open, type);

  return (
    <AppSider
      title={getSiderTitle(type)}
      isSiderVisible={open}
      hideSider={onClose}
      isFooterVisible={false}
      submitBtnText=""
      cancelBtnText=""
      contentClassName={styles.violationsDrawerRoot}
    >
      <AppFlex direction="column" unit="16">
        <Collapse ghost activeKey={activeKeys}>
          {violations.map((violation, violationIndex) => {
            const panelKey = `${violation.category}-${violation.code}-${violationIndex}`;
            const isExpanded = activeKeys.includes(panelKey);
            return (
              <Collapse.Panel
                key={panelKey}
                header={
                  <Alert
                    message={
                      <div className={styles.alertHeaderRow}>
                        <span className={styles.alertHeaderText}>
                          {`${getViolationLabel(violation.code)}: ${
                            violation.timeslotGroups?.length
                          }`}
                        </span>
                        <span
                          className={styles.alertExpandToggle}
                          aria-expanded={isExpanded}
                          aria-hidden
                        >
                          <RightOutlined
                            rotate={isExpanded ? 90 : undefined}
                            className={styles.alertExpandIcon}
                          />
                        </span>
                      </div>
                    }
                    className={styles.errorLabel}
                    type={violation.category === 'LOGICAL' ? 'error' : 'warning'}
                    onClick={() => togglePanelKey(panelKey)}
                    showIcon
                  />
                }
                className={styles.collapsePanel}
                showArrow={false}
              >
                <AppFlex direction="column" unit="30" className={styles.lessonGroupsStack}>
                  {violation?.timeslotGroups?.map((lessonGroup, groupIndex) => (
                    <AppFlex
                      direction="column"
                      unit="8"
                      key={`${lessonGroup?.id}-${groupIndex}`}
                      className={styles.lessonGroupBlock}
                    >
                      {lessonGroup.timeslots.map((lesson) => {
                        return (
                          <AppFlex
                            direction="column"
                            unit="4"
                            className={styles.lessonContainer}
                            key={`${lessonGroup.id}-${lesson.dayOfWeek}-${lesson.startTime}-${lesson.endTime}-${lesson.educationalClass.id}`}
                            onClick={() => handleViolationLessonClick(lesson)}
                          >
                            <AppText className={styles.subjectName}>
                              {lesson?.subject?.name}
                            </AppText>
                            <AppText type="secondary" className={styles.teacherCode}>
                              {lesson?.teacher?.code}
                            </AppText>
                            <AppFlex direction="row" unit="4">
                              <AppText>{translateDay(lesson?.dayOfWeek as DayOfWeek)}</AppText>
                              <AppText>{`${lesson.startTime} - ${lesson.endTime}`}</AppText>
                              <AppText>{`/ ${
                                educationParallelTextRepresentation[
                                  lesson?.educationalClass?.parallel
                                ]
                              }${lesson?.educationalClass?.litera}`}</AppText>
                            </AppFlex>
                          </AppFlex>
                        );
                      })}
                    </AppFlex>
                  ))}
                </AppFlex>
              </Collapse.Panel>
            );
          })}
        </Collapse>
      </AppFlex>
    </AppSider>
  );
};
